import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, TextField, Button, IconButton, Modal,
    Typography, Paper, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchPackagesFromAPI,
    createPackageInAPI,
    updatePackageInAPI,
    deletePackageFromAPI
} from '../../../Redux/TokenPackagesDucks';

const formatCLP = (v) => '$' + (Number(v) || 0).toLocaleString('es-CL');

const TokenPackagesPage = () => {
    const dispatch = useDispatch();
    const packages = useSelector(store => store.tokenPackages.packages);

    const [loading, setLoading] = useState(true);
    const [filterText, setFilterText] = useState('');
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [packageToDelete, setPackageToDelete] = useState(null);

    // Form state
    const [name, setName] = useState('');
    const [tokenAmount, setTokenAmount] = useState('');
    const [price, setPrice] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        await dispatch(fetchPackagesFromAPI());
        setLoading(false);
    };

    const resetForm = () => {
        setName('');
        setTokenAmount('');
        setPrice('');
    };

    const handleAdd = () => {
        setSelectedPackage(null);
        resetForm();
        setIsModalOpen(true);
    };

    const handleEdit = (pkg) => {
        setSelectedPackage(pkg);
        setName(pkg.name || '');
        setTokenAmount(pkg.tokenAmount != null ? String(pkg.tokenAmount) : '');
        setPrice(pkg.price != null ? String(pkg.price) : '');
        setIsModalOpen(true);
    };

    const handleDelete = (pkg) => {
        setPackageToDelete(pkg);
        setIsDeleteDialogOpen(true);
    };

    const handleSave = async () => {
        const dto = {
            name,
            tokenAmount: parseInt(tokenAmount) || 0,
            price: parseFloat(price) || 0,
        };
        if (selectedPackage) {
            await dispatch(updatePackageInAPI(selectedPackage.id, dto));
        } else {
            await dispatch(createPackageInAPI(dto));
        }
        setIsModalOpen(false);
        loadData();
    };

    const confirmDelete = () => {
        dispatch(deletePackageFromAPI(packageToDelete.id));
        setIsDeleteDialogOpen(false);
    };

    const getStatusText = (active) => active ? 'Activo' : 'Inactivo';

    const columns = [
        { name: 'Nombre', selector: row => row.name, sortable: true, grow: 2 },
        { name: 'Tokens', selector: row => row.tokenAmount, sortable: true },
        { name: 'Precio', selector: row => row.price, sortable: true, cell: row => formatCLP(row.price) },
        {
            name: 'Precio/token', selector: row => row.price,
            cell: row => row.tokenAmount > 0 ? formatCLP(Math.round(row.price / row.tokenAmount)) : '-',
        },
        {
            name: 'Estado', selector: row => getStatusText(row.active), sortable: true,
            cell: row => (
                <span className={`apple-badge ${row.active ? 'apple-badge-success' : 'apple-badge-error'}`}>
                    {getStatusText(row.active)}
                </span>
            ),
        },
        {
            name: 'Acciones',
            cell: row => (
                <Box>
                    <IconButton onClick={() => handleEdit(row)} size="small" className="apple-button apple-tooltip">
                        <EditIcon fontSize="small" />
                        <span className="tooltiptext">Editar</span>
                    </IconButton>
                    <IconButton onClick={() => handleDelete(row)} size="small" className="apple-button apple-tooltip">
                        <RemoveCircleOutlineIcon fontSize="small" color="error" />
                        <span className="tooltiptext">Desactivar</span>
                    </IconButton>
                </Box>
            ),
            width: '160px',
        },
    ];

    const customStyles = {
        headRow: { style: { backgroundColor: '#F2F2F7', borderBottom: 'none' } },
        headCells: { style: { fontSize: '12px', fontWeight: '600', color: '#86868B', textTransform: 'uppercase' } },
        cells: { style: { fontSize: '14px', color: '#1D1D1F' } },
    };

    const filteredItems = (Array.isArray(packages) ? packages : []).filter(
        item => item.name && item.name.toLowerCase().includes(filterText.toLowerCase())
    );

    const subHeaderComponent = useMemo(() => (
        <Box className="apple-card" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 2 }}>
            <div className="apple-search-container">
                <SearchIcon className="apple-search-icon" />
                <input
                    type="text"
                    placeholder="Buscar paquete"
                    onChange={e => setFilterText(e.target.value)}
                    className="apple-search-input"
                />
            </div>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} className="apple-button">
                Agregar Paquete
            </Button>
        </Box>
    ), [filterText]);

    return (
        <div className="apple-container">
            <Paper className="apple-card">
                <DataTable
                    columns={columns}
                    data={filteredItems}
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 20, 30]}
                    subHeader
                    subHeaderComponent={subHeaderComponent}
                    progressPending={loading}
                    persistTableHead
                    customStyles={customStyles}
                />
            </Paper>

            <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <Box className="apple-modal" sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 600 }}>
                    <Typography className="apple-modal-title">
                        {selectedPackage ? 'Editar Paquete de Tokens' : 'Agregar Paquete de Tokens'}
                    </Typography>
                    <div className="apple-modal-content">
                        <TextField label="Nombre *" variant="outlined" fullWidth className="apple-input" value={name} onChange={e => setName(e.target.value)} />
                        <TextField label="Cantidad de tokens *" type="number" variant="outlined" fullWidth className="apple-input" value={tokenAmount} onChange={e => setTokenAmount(e.target.value)} />
                        <TextField label="Precio (CLP) *" type="number" variant="outlined" fullWidth className="apple-input" value={price} onChange={e => setPrice(e.target.value)} />
                    </div>
                    <div className="apple-modal-footer">
                        <Button onClick={() => setIsModalOpen(false)} className="apple-button">Cancelar</Button>
                        <Button variant="contained" className="apple-button" onClick={handleSave} disabled={!name || !tokenAmount || !price}>
                            {selectedPackage ? 'Guardar Cambios' : 'Crear Paquete'}
                        </Button>
                    </div>
                </Box>
            </Modal>

            <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
                <DialogTitle>Confirmar desactivación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea desactivar el paquete <strong>{packageToDelete?.name}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteDialogOpen(false)} className="apple-button">Cancelar</Button>
                    <Button onClick={confirmDelete} className="apple-button" autoFocus>Confirmar</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default TokenPackagesPage;
